package xim.poc.game.configuration.v0.tactics

import xim.math.Vector2f
import xim.poc.audio.SystemSound
import xim.poc.game.*
import xim.poc.game.configuration.v0.HelpNotifier
import xim.poc.game.configuration.v0.TacticsInstinct
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.game.configuration.v0.tactics.TacticsState.getMaxInstincts
import xim.poc.ui.ChatLog
import xim.poc.ui.ChatLogColor
import xim.poc.ui.ShiftJis
import kotlin.time.Duration.Companion.seconds

class TacticsInstinctConsumeUi(
    val unit: TacticsUnit,
    val instinctOptions: List<TacticsInstinct>,
    val onCompleteCallback: () -> Unit,
) {

    private lateinit var root: UiStateFrame

    fun push() {
        root = UiStateHelper.openQueryMode(
            prompt = "Consume an instinct?",
            options = makeOptions(),
            drawFn = { drawUnitDescription(); drawTip(it) },
            enableCursorMovementKeys = true,
            visibleOptions = 4,
        ) { handleSelection(it) }
    }

    private fun makeOptions(): List<QueryMenuOption> {
        val options = ArrayList<QueryMenuOption>()

        for (instinct in instinctOptions) {
            options += QueryMenuOption(instinct.display(), instinct.id)
        }

        options += QueryMenuOption("Skip!", -1)
        return options
    }

    private fun handleSelection(option: QueryMenuOption?): QueryMenuResponse {
        if (option == null || option.value < 0) {
            onCompleteCallback()
            return QueryMenuResponse.pop
        }

        val instinct = instinctOptions.firstOrNull { it.id == option.value }
        if (instinct == null) {
            onCompleteCallback()
            return QueryMenuResponse.pop
        }

        val matchingInstinct = unit.instincts.firstOrNull { it.id == instinct.id }
        if (matchingInstinct != null) {
            confirmOverwrite(instinct, matchingInstinct)
            return QueryMenuResponse.noop
        }

        if (unit.instincts.size == getMaxInstincts()) {
            askOverwrite(instinct)
            return QueryMenuResponse.noop
        }

        learnInstinct(instinct)
        return QueryMenuResponse.noop(SystemSound.FishSuccess)
    }

    private fun drawUnitDescription() {
        TacticsUnitDescription.drawUnitDescription(unit, ActorType.AllyNpc)
    }

    private fun drawTip(option: QueryMenuOption?) {
        val parentLatestPos = root.state.latestPosition ?: return
        val parentLatestSettings = root.state.latestMenu ?: return
        val toolTipPosition = Vector2f(parentLatestPos.x + parentLatestSettings.frame.size.x + 2f, parentLatestPos.y)

        val instinct = instinctOptions.firstOrNull { it.id == option?.value } ?: return
        ToolTipHelper.drawUiSpaceToolTip(toolTipPosition, instinct.toolTip())
    }

    private fun askOverwrite(instinct: TacticsInstinct) {
        UiStateHelper.openQueryMode(
            prompt = "Overwrite which instinct?",
            options = makeOverwriteOptions(),
            drawFn = { drawUnitDescription() },
            enableCursorMovementKeys = true,
            visibleOptions = 4,
        ) { handleOverwriteSelection(instinct, it) }

    }

    private fun makeOverwriteOptions(): List<QueryMenuOption> {
        val options = ArrayList<QueryMenuOption>()

        for (instinct in unit.instincts) {
            options += QueryMenuOption(instinct.display(), instinct.id)
        }

        options += QueryMenuOption("Back.", -1)
        return options
    }

    private fun handleOverwriteSelection(instinct: TacticsInstinct, option: QueryMenuOption?): QueryMenuResponse {
        if (option == null || option.value < 0) { return QueryMenuResponse.pop }

        val matching = unit.instincts.firstOrNull { it.id == option.value }
            ?: return QueryMenuResponse.pop

        confirmOverwrite(instinct, matching)
        return QueryMenuResponse.noop
    }

    private fun confirmOverwrite(instinct: TacticsInstinct, matchingInstinct: TacticsInstinct) {
        val options = listOf(
            QueryMenuOption("No.", -1),
            QueryMenuOption("Yes!", 0),
        )

        UiStateHelper.openQueryMode(
            prompt = "Overwrite ${ShiftJis.colorInfo}${matchingInstinct.display()}${ShiftJis.colorClear}?",
            options = options,
            drawFn = { drawUnitDescription() },
            enableCursorMovementKeys = true,
        ) {
            if (it == null || it.value < 0) {
                QueryMenuResponse.pop
            } else {
                learnInstinct(instinct, matchingInstinct)
                QueryMenuResponse.noop(SystemSound.FishSuccess)
            }
        }
    }

    private fun learnInstinct(newInstinct: TacticsInstinct, overwriteInstinct: TacticsInstinct? = null) {
        onCompleteCallback()
        UiStateHelper.popTo(root.parent?.state)

        val message = "${unit.name} gained ${ShiftJis.colorAug}${newInstinct.display()}${ShiftJis.colorClear}!"
        ChatLog(message, ChatLogColor.Info)
        HelpNotifier.notify(message, 2.seconds)

        val currentInstincts = unit.instincts
        for (i in currentInstincts.indices) {
            val currentInstinct = currentInstincts[i]

            if (currentInstinct.id == newInstinct.id || currentInstinct.id == overwriteInstinct?.id) {
                currentInstincts[i] = newInstinct
                return
            }
        }

        if (currentInstincts.size < getMaxInstincts()) {
            currentInstincts += newInstinct
        }
    }

}